import { ReactComponent as IconInfo } from '@/assets/svgs/api-configuration__icon.svg';
import { ReactComponent as IconURLName } from '@/assets/svgs/api-configuration_url-icon.svg';
import { ReactComponent as SelectedSourceSystemIcon } from '@/assets/svgs/selected-sourcesystem-icon.svg';
import { ReactComponent as IconSourceSystem } from '@/assets/svgs/source-system-default.svg';
import { ReactComponent as SourceSystemIcon } from '@/assets/svgs/sourcesystem-icon.svg';
import Button from '@/components/Button';
import Dropdown from '@/components/Dropdown';
import CardConfigure from '@/componentsDS/Modal/CardConfigure';
import Modal from '@/componentsDS/Modal/Modal';
import MultiLevelDropDown from "@/components/MultiLevelMenuDropDown/index.js";
import Select from '@/components/Select';
import Tabs, { Tab } from '@/components/Tabs';
import Toast2 from '@/components/Toast2';
import { ToastContext } from "@/context/toast/index.js";
import useModal from '@/hooks/use-modal/index';
import usePage from '@/hooks/use-page';
import usePitstopConfig from '@/hooks/use-pitstop-config';
import usePublicKey from '@/hooks/use-public-key';
import useSourceSystemConfig from '@/hooks/use-sourceSystem-config/index.js';
import { useLocation } from '@/lib/router-dom';
import CONFIG_API from '@/services/api/config.js';
import api from '@/services/api/pitstopConfig.js';
import apiSystemConfig from '@/services/api/systemConfig.js';
import "@/translations/i18n";
import rem from '@/utils/rem.js';
import { uniqueId } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from "react-i18next";
import { CreateSourceSystemModal, DeleteSourceSystemModal, SetDefaultSourceSystemModal } from "./models";
import MultiConfigList from './multiple-config-lists.jsx';
import Breadcrumbs from '@/componentsDS/Breadcrumbs/index';
import { useBuildex } from '@/hooks/use-buildex/index';

const Page = () => {

	/*=================================================
		General
	/================================================*/

	const { t } = useTranslation();
	const [pageTitle] = useState("API Configurations")
	const isBuildex = useBuildex()

	const TypeEnum = { "PROVIDE": "Provide", "RECEIVE": "Receive", "NOTIFY": "Notify", "NODE": "Comments/Status", "CREATE_PACKAGE": "Create Data Package", "CREATE_FOLDER": "Create Folder", "UPDATE_PACKAGE": "Update Data Package" }
	const EnumAllowedForBuildex =["PROVIDE", "RECEIVE", "NOTIFY"]

	const { breadcrumbsItems } = usePage({
		title: pageTitle,
		breadcrumbs: [
			{
				label: '',
				link: `/`,
			},
			{
				label: 'Configuration',
				link: `/configuration/api-configuration`
			},
		]
	 })
	const { currentState } = useLocation();

	const { register, handleSubmit, formState: { isValid } } = useForm({ mode: 'onChange' })

	const [tabsItems, setTabsItems] = useState([
		{ label: t('sendingToPitstop'), active: true, id: "api-configuration-sending-to-pitstop" },
		{ label: t('sendingToSourceSystem'), active: false, id: "api-configuration-sending-to-source-system" },
	])

	const [subTabsItems, setSubTabsItems] = useState([])

	const [pitstopConfig, setPitstopConfig] = useState()
	const [pitstopApiKey, setPitstopApiKey] = useState("")
	const [pitstopJwtKey, setPitstopJwtKey] = useState()
	const [adminUrl, setAdminUrl] = useState("")
	const [pitstopPublicKey, setPitstopPublicKey] = useState()
	const [jwtSourceSystemPublicKey, setJwtSourceSystemPublicKey] = useState()
	const [jwtJweSourceSystemPublicKey, setJwtJweSourceSystemPublicKey] = useState()
	const [payload, setPayload] = useState();

	const { addToastMessage } = useContext(ToastContext);

	/*=================================================
		Error Modal
	/================================================*/
	const [error, setError] = useState()

	function handleError() {
		setError()
	}

	/*=================================================
		API
	/================================================*/
	const { sourceSystemsItems, sourceSystemsFilters, fetchSourceSystem, fetchFilters } = useSourceSystemConfig()
	const { fetchApiKey, fetchJwtKey, fetch: fetchPitstopConfig } = usePitstopConfig()
	const { fetch: fetchPublicKey } = usePublicKey()

	const [authType, setAuthType] = useState("NONE")
	const [jwtIssuer, setJwtIssuer] = useState("")

	const dataElementsOptions = [
		{ label: 'None', value: 'NULL' },
		{ label: 'No Auth', value: 'NONE' },
		{ label: 'API Key', value: 'API_KEY' },
		{ label: 'JWT (Symmetric)', value: 'JWT_SYM' },
		{ label: 'JWT (Asymmetric)', value: 'JWT' },
		{ label: 'JWT and JWE', value: 'JWT_JWE' },
	]

	const [selectedOption, setOption] = useState("None")
	const { visible: isSubmitModalVisible, show: showApiSubmitModal, hide: hideApiSubmitModal } = useModal()

	const { visible: isCreateSourceSystemVisible, show: showCreateSourceSystemModal, hide: hideCreateSourceSystemModal } = useModal();
	const { visible: isDeleteSourceSystemVisible, show: showDeleteSourceSystemModal, hide: hideDeleteSourceSystemModal } = useModal();
	const { visible: isSetDefaultSourceSystemVisible, show: showSetDefaultSourceSystemModal, hide: hideSetDefaultSourceSystemModal } = useModal();

	const [selectedSourceSystem, setSelectedSourceSystem] = useState(null)

	useEffect(() => {
		if (currentState?.tab === 2) selectTab(1)
	}, [])

	useEffect(() => {
		async function getData() {
			setPitstopPublicKey(await fetchPublicKey())
			setPitstopConfig(await fetchPitstopConfig())

			const getAdminUrl = await CONFIG_API.checkStatus()
			setAdminUrl(getAdminUrl[1]?.adminBaseUrl)
		}
		getData()
	}, [])

	useEffect(() => {
		if (pitstopConfig?.type === "NONE") {
			setAuthType("NONE")
			setOption("No Auth")
		}
		if (pitstopConfig?.type === "API_KEY") {
			setPitstopApiKey(pitstopConfig["SGTRADEX-API-KEY"])
			setAuthType("API_KEY")
			setOption("API_KEY")
		}
		if (pitstopConfig?.type === "JWT_SYM") {
			setPitstopJwtKey(pitstopConfig["Authorization"])
			setAuthType("JWT_SYM")
			setOption("JWT_SYM")
		}
		if (pitstopConfig?.type === "JWT") {
			setJwtSourceSystemPublicKey(pitstopConfig?.publicKey)
			setAuthType("JWT")
			setOption("JWT")
		}
		if (pitstopConfig?.type === "JWT_JWE") {
			setJwtJweSourceSystemPublicKey(pitstopConfig?.jwt?.publicKey)
			setJwtIssuer(pitstopConfig.jwt?.iss);
			setAuthType("JWT_JWE")
			setOption("JWT_JWE")
		}
	}, [pitstopConfig])

	/*=================================================
		Tabs
	/================================================*/

	function selectTab(index) {
		const tabs = [...tabsItems].map(item => ({ ...item, active: false }))

		tabs[index].active = true
		setTabsItems(tabs)
	}

	function filterApiConfiguration(item){
		return item?.APIConfigurations?.filter(config => EnumAllowedForBuildex.includes(config))
	}

	function selectSubTab(value, index) {
		const tabs = [...subTabsItems].map(item => ({ ...item, active: false }))
		tabs[index].active = true
		setSubTabsItems(tabs)
		fetchSourceSystem(value);
	}

	useEffect(() => {
		fetchSourceSystem("");
		fetchFilters();
	}, [])

	useEffect(() => {
		let tabItemsArray = [];
		sourceSystemsFilters?.map((item) => {
			return tabItemsArray.push({ label: TypeEnum[item], active: false, value: item })
		})
		if (tabItemsArray.length > 1) {
			setSubTabsItems([...[{ label: "All", active: true, value: '' }], ...tabItemsArray]);
		}
		else if (tabItemsArray.length === 1) {
			tabItemsArray[0].active = true;
			setSubTabsItems(tabItemsArray);
		}
		else {
			setSubTabsItems([])
		}
	}, [sourceSystemsFilters])

	function handleValue(option) {
		setAuthType(option.value)
		setOption(option.label)
		handleAuthSelect(option.value)
	}

	function deleteConfigTypeLists() {
		fetchSourceSystem("");
		fetchFilters();
	}

	/*=================================================
		Data Table
	/================================================*/

	const DataTable = ({ data }) => {

		const sourceSystemLists = data?.map((item, index) => {
			const id = uniqueId("source-system-")
			if (isBuildex && item?.APIConfigurations) {
				item.APIConfigurations = filterApiConfiguration(item)
			}

			return (
				<div className="source-system__container" key={id}>
					<div className='source-system__heading-content'>
						<div className='source-system__heading__left-content DS__icon--theme'>
							<IconSourceSystem />
							<span className='source-system__heading-left-content__text'>
								{item?.name}
								{item?.isDefault && <span>{" (Default)"}</span>}
							</span>
							<span className='source-system__heading-content__icon-dropdown'>
								<MultiLevelDropDown
									placement="bottom-start"
									menuList={[
										{
											label: "Delete Source System",
											onClick: (e) => {
												e.stopPropagation();
												setSelectedSourceSystem({ id: item.id });
												showDeleteSourceSystemModal();
											},
										}
									]}
								/>
							</span>
						</div>
						<div className='source-system__heading__right-content'>
							{item?.isDefault && <div className='source-system__heading__selected-icon DS__icon--radio-icon'><SelectedSourceSystemIcon /></div>}
							{!item?.isDefault && <div className='source-system__heading__default-icon DS__icon--radio-icon--uncheck' onClick={() => { setSelectedSourceSystem({ id: item.id }); showSetDefaultSourceSystemModal(); }}>
								<SourceSystemIcon />
							</div>}
							<span className='source-system__heading-right-content__text'>Set Default Source System</span>
							<div className='source-system__heading__info-icon tooltip DS__icon--theme'>
								<IconInfo />
								<span className="tooltiptext tooltiptext__copy-label">
									If there is more than one source system, <br /> system will take the selected source as default source system.
								</span>
							</div>
						</div>
					</div>
					<MultiConfigList
						item={item}
						handleSubmit={deleteConfigTypeLists}
					/>
				</div>
			)
		})

		return (
			sourceSystemLists
		)
	}

	/*=================================================
		Functions
	/================================================*/

	async function formHandler(data) {
		let payload = {};

		if (authType === "NONE") {
			payload = {
				"type": "NONE",
			}
		}

		if (authType === "NULL") {
			payload = {
				"type": "NULL",
			}
		}

		if (authType === "API_KEY") {
			payload = {
				"type": "API_KEY",
				"SGTRADEX-API-KEY": data.api_key_value
			}
		}

		if (authType === "JWT_SYM") {
			payload = {
				"type": "JWT_SYM",
				"Authorization": data.jwt_key_value
			}
		}

		if (authType === "JWT") {
			payload = {
				"type": "JWT",
				"alg": data.JWT_alg,
				"publicKey": data.ss_public_key
			}
		}

		if (authType === "JWT_JWE") {
			payload = {
				"type": "JWT_JWE",
				"jwt": {
					"alg": data.JWT_alg,
					"iss": data.jwt_jwe_jwt_iss,
					"publicKey": data.ss_public_key
				},
				"jwe": {
					"alg": data.JWE_alg,
					"enc": data.JWT_enc
				}

			}
		}
		setPayload(payload);
		showApiSubmitModal()
	}

	async function updatePitstopConfig(payload) {
		const [error] = await api.update(payload);

		if (error) {
			return setError(error?.message || "Failed to update Pitstop Config")
		}
		window.location.replace(window.location.href)
	}

	async function handleCreateSourceSystemSubmit(name, isDefault) {
		const [err] = await apiSystemConfig.create({
			name,
			isDefault
		});

		if (err) {
			return setError(err?.message || "Failed to Create Source System")
		}

		hideCreateSourceSystemModal();
		addToastMessage("Source system added successfully", 'success')
		fetchSourceSystem("");
	}

	async function handleDeleteSourceSystemSubmit() {
		const [error, _] = await apiSystemConfig.remove(selectedSourceSystem.id);

		if (error) {
			return setError(error.message || "Failed to Delete Source System")
		}

		hideDeleteSourceSystemModal();
		addToastMessage("Source system deleted successfully", 'success')
		fetchSourceSystem("");
		fetchFilters();
	}

	async function handleSetDefaultSourceSystemSubmit() {
		const [err, _] = await apiSystemConfig.updateById(selectedSourceSystem.id, { isDefault: true });

		if (err) {
			return setError(err?.message || "Failed to Update Source System")
		}

		hideSetDefaultSourceSystemModal();
		addToastMessage("Default source system updated", 'success')
		fetchSourceSystem("");
	}

	function handleAuthSelect(e) {
		// setAuthType(e.target.value)
		setAuthType(e)


		if (pitstopConfig?.type !== "API_KEY") {
			setPitstopApiKey("");
		}

		if (pitstopConfig?.type !== "JWT_SYM") {
			setPitstopJwtKey("");
		}

		if (e === "API_KEY" && pitstopConfig?.type !== "API_KEY") {
			async function getData() {
				const getApiKey = await fetchApiKey()
				setPitstopApiKey(getApiKey.api_key);
			}
			getData()
		}

		if (e === "JWT_SYM" && pitstopConfig?.type !== "JWT_SYM") {
			async function getData() {
				const getJwtKey = await fetchJwtKey()
				setPitstopJwtKey(getJwtKey.token);
			}
			getData()
		}
	}

	return (
		<>
			<Breadcrumbs breadcrumbItems={breadcrumbsItems} />

			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__title">
						{pageTitle}
					</div>
				</div>
				{(tabsItems[1].active) && <div className="title-header__right">
					<Button sizing='Medium' round text={t('+ Add Source System')} id="configuration-api-configuration-add-source-system" full maxWidth='182' type="button" submit={() => showCreateSourceSystemModal()} />
				</div>}
			</div>

			<div className="api-configuration__header">
				<Tabs>
					{tabsItems.map((item, index) =>{
						const id = uniqueId('tab_')
						return (
							<Tab key={id} label={item.label} active={item.active} onClick={() => selectTab(index)} id={item.id} />
						)
					})}
				</Tabs>
			</div>

			<div className="title-header__hr">
				<hr />
			</div>
			{
				(tabsItems[0].active) && (
					<>
						<div className="pitstop-sourcesystem__cols">
							<div className="pitstop-sourcesystem__col">
								<div className="pitstop-sourcesystem__col__left pitstop-sourcesystem_col_left_label">
									Authentication Type
								</div>
								<div className="pitstop-sourcesystem__col__right pitstop-sourcesystem__col__right_input_wrap pitstop-sourcesystem__col__right_dropdown">
									<Dropdown value={selectedOption}>
										{dataElementsOptions?.map((option, index) => {
											if (adminUrl && (adminUrl === "https://admin.pre-prod.sgtradex.io/" || adminUrl === "https://admin.sgtradex.io/")) {
												if (option.value === "NONE") {
													return (
														<Dropdown.Item key={option.value} disabled>
															{option.label}
														</Dropdown.Item>
													);
												}
											}
											return (
												<Dropdown.Item key={option.value} onClick={() => handleValue(option)}>
													{option.label}
												</Dropdown.Item>
											)
										})}
									</Dropdown>

								</div>
							</div>
							<form id="pitstopConfigAuth" onSubmit={handleSubmit(formHandler)}>
								{
									(authType === "API_KEY") && (
										<>
											<div className="pitstop-sourcesystem__col">
												<div className="pitstop-sourcesystem__col__left pitstop-sourcesystem_col_left_label">
													HTTP Header Key
												</div>
												<div className="pitstop-sourcesystem__col__right pitstop-sourcesystem__col__right_input_wrap">
													<div className="pitstop-sourcesystem__col__right_input pitstop-sourcesystem__col__right_input--disabled">
														<input name="api_key_value_input" type="text" readOnly value="SGTRADEX-API-KEY" ref={register({ required: authType === "API_KEY" })} />
													</div>
												</div>
											</div>
											<div className="pitstop-sourcesystem__col">
												<div className="pitstop-sourcesystem__col__left pitstop-sourcesystem_col_left_label">
													HTTP Body Key
												</div>
												<div className="pitstop-sourcesystem__col__right pitstop-sourcesystem__col__right_input_wrap pitstop-sourcesystem__col__right--flex pitstop-sourcesystem__col__right--flex_end">
													<div className="pitstop-sourcesystem__col__right_input pitstop-sourcesystem__col__right_input--disabled">
														<input name="api_key_value" type="text" readOnly value={pitstopApiKey || "Generating"} ref={register({ required: authType === "API_KEY" })} />
													</div>
													<div className="pitstop-sourcesystem__col_button">
														<Button ghost type="button" text="Copy" submit={ () => { navigator.clipboard.writeText(pitstopApiKey) }} />
													</div>
												</div>
											</div>
										</>
									)
								}
								{
									(authType === "JWT_SYM") && (
										<>
											<div className="pitstop-sourcesystem__col">
												<div className="pitstop-sourcesystem__col__left pitstop-sourcesystem_col_left_label">
													Authorization
												</div>
												<div className="pitstop-sourcesystem__col__right pitstop-sourcesystem__col__right--flex pitstop-sourcesystem__col__right_input_wrap">
													<div className="pitstop-sourcesystem__col__right_input_bearer">
														<input disabled value="Bearer" />
													</div>
													<div className="pitstop-sourcesystem__col__right_input_authorization pitstop-sourcesystem__col__right_input--disabled">
														<input name="jwt_key_value" type="text" readOnly value={pitstopJwtKey || "Generating "} ref={register({ required: authType === "JWT_SYM" })} />
													</div>
													<div className="pitstop-sourcesystem__col_button">
														<Button ghost type="button" text="Copy" submit={ () => { navigator.clipboard.writeText(pitstopJwtKey) }} />
													</div>
												</div>
											</div>
										</>
									)
								}
								{
									(authType === "JWT") && (
										<>
											<div className="pitstop-sourcesystem__col">
												<div className="pitstop-sourcesystem__col__left pitstop-sourcesystem_col_left_label">
													JWT Algorithm
												</div>
												<div className="pitstop-sourcesystem__col__right pitstop-sourcesystem__col__right--flex pitstop-sourcesystem__col__right_input_wrap">
													<Select containerProps={{ style: { width: rem(366) } }} selectProps={{ onChange: (e) => { handleAuthSelect(e) }, value: "RS256", name: "JWT_alg_select", disabled: true }}>
														<option value="" disabled>{t("select")}</option>
														<option value="RS256">RS256</option>
													</Select>
													<input type="hidden" name="JWT_alg" value="RS256" ref={register({ required: authType === "JWT" })} />
													<div className="pitstop-sourcesystem__col_button">
														<Button ghost text="Copy" type="button" submit={ () => { navigator.clipboard.writeText('RS256') }} />
													</div>

												</div>
											</div>
											<div className="pitstop-sourcesystem__col pitstop-sourcesystem__col--top-align">
												<div className="pitstop-sourcesystem__col__left pitstop-sourcesystem__col__left--margin-top pitstop-sourcesystem_col_left_label">
													Source System Public Key
												</div>
												<div className="pitstop-sourcesystem__col__right pitstop-sourcesystem__col__right_input_wrap">
													<div className="pitstop-sourcesystem__col__right_input">
														<textarea name="ss_public_key" value={jwtSourceSystemPublicKey} onChange={(e) => setJwtSourceSystemPublicKey(e.target.value)} ref={register({ required: authType === "JWT" })} autoComplete="off" />
													</div>
												</div>
											</div>
										</>
									)
								}
								{
									(authType === "JWT_JWE") && (
										<>
											<div className="pitstop-sourcesystem__col">
												<div className="pitstop-sourcesystem__col__left pitstop-sourcesystem_col_left_label">
													JWT Algorithm
												</div>
												<div className="pitstop-sourcesystem__col__right pitstop-sourcesystem__col__right--flex pitstop-sourcesystem__col__right_input_wrap">
													<Select containerProps={{ style: { width: rem(366) } }} selectProps={{ value: "RS256", name: "JWT_alg_select", disabled: true }}>
														<option value="" disabled>{t("select")}</option>
														<option value="RS256">RS256</option>
													</Select>
													<input type="hidden" name="JWT_alg" value="RS256" ref={register({ required: authType === "JWT_JWE" })} />
												</div>
											</div>
											<div className="pitstop-sourcesystem__col">
												<div className="pitstop-sourcesystem__col__left pitstop-sourcesystem_col_left_label">
													JWT Issuer
												</div>
												<div className="pitstop-sourcesystem__col__right pitstop-sourcesystem__col__right_input_wrap">
													<div className="pitstop-sourcesystem__col__right_input">
														<input name="jwt_jwe_jwt_iss" value={jwtIssuer} onChange={(e) => setJwtIssuer(e.target.value)} type="text" ref={register({ required: authType==="JWT_JWE" })} />
													</div>
												</div>
											</div>

											<div className="pitstop-sourcesystem__col pitstop-sourcesystem__col--top-align">
												<div className="pitstop-sourcesystem__col__left pitstop-sourcesystem__col__left--margin-top pitstop-sourcesystem_col_left_label">
													Cryptographic Algorithm
												</div>
												<div className="pitstop-sourcesystem__col__right pitstop-sourcesystem__col__right_input_wrap">
													<Select containerProps={{ style: { width: rem(366) } }} selectProps={{ value: "RSA-OAEP-256", name: "JWE_alg_select", disabled: true }}>
														<option value="" disabled>{t("select")}</option>
														<option value="RSA-OAEP-256">RSA-OAEP-256</option>
													</Select>
													<input type="hidden" name="JWE_alg" value="RSA-OAEP-256" ref={register({ required: authType === "JWT_JWE" })} />
												</div>
											</div>
											<div className="pitstop-sourcesystem__col pitstop-sourcesystem__col--top-align">
												<div className="pitstop-sourcesystem__col__left pitstop-sourcesystem__col__left--margin-top pitstop-sourcesystem_col_left_label">
													Symmetric Encryption Algorithm
												</div>
												<div className="pitstop-sourcesystem__col__right pitstop-sourcesystem__col__right_input_wrap">
													<Select containerProps={{ style: { width: rem(366) } }} selectProps={{ value: "A256CBC-HS512", name: "JWE_enc_select", disabled: true }}>
														<option value="" disabled>{t("select")}</option>
														<option value="A256CBC-HS512">A256CBC-HS512</option>
													</Select>
													<input type="hidden" name="JWT_enc" value="A256CBC-HS512" ref={register({ required: authType === "JWT_JWE" })} />
												</div>
											</div>
											<div className="pitstop-sourcesystem__col pitstop-sourcesystem__col--top-align">
												<div className="pitstop-sourcesystem__col__left pitstop-sourcesystem__col__left--margin-top pitstop-sourcesystem_col_left_label">
													Pitstop Public Key
												</div>
												<div className="pitstop-sourcesystem__col__right pitstop-sourcesystem__col__right_input_wrap">
												<div className="pitstop-sourcesystem__col__right_input">
													<textarea disabled
														value={pitstopPublicKey?.encPublicKey}
													/>
												</div>
												</div>
											</div>
											<div className="pitstop-sourcesystem__col pitstop-sourcesystem__col--top-align">
												<div className="pitstop-sourcesystem__col__left pitstop-sourcesystem__col__left--margin-top pitstop-sourcesystem_col_left_label">
													Source System Public Key
												</div>
												<div className="pitstop-sourcesystem__col__right pitstop-sourcesystem__col__right_input_wrap">
													<div className="pitstop-sourcesystem__col__right_input">
														<textarea name="ss_public_key"
															value={jwtJweSourceSystemPublicKey}
															onChange={(e) => setJwtJweSourceSystemPublicKey(e.target.value)}
															ref={register({ required: authType === "JWT_JWE" })}
															autoComplete="off" />
													</div>
												</div>
											</div>
										</>
									)
								}
							</form>
						</div>
						<div className="DS__layout__footer">
							<div className="layout-default__sticky-actions">
								<div className="layout-default__sticky-actions__left">
								</div>
								<div className="layout-default__sticky-actions__right">

									<Button sizing='Medium' form="pitstopConfigAuth" text={t('save')} full maxWidth='154'
										disabled={!isValid}
									/>
								</div>
							</div>
						</div>
					</>
				)
			}
			{
				(tabsItems[1].active) && (
					<>
						<div className="overview__content">
							<div className="api-configuration">
								<div className="api-configuration__row">
									<div className="api-configuration__left">
										<div className="api-configuration__subheader">
											<Tabs>
												{subTabsItems.map((item, index) => {
													const id = uniqueId('subtab_')
													return (
														<Tab key={id} label={item.label} active={item.active} onClick={() => selectSubTab(item.value, index)} />
													)
												})}
											</Tabs>
										</div>
									</div>
								</div>
								<DataTable data={sourceSystemsItems} />
							</div>
						</div>
					</>
				)
			}

			{/* Submit API CONFIG */}
			<Modal visible={isSubmitModalVisible} variant='medium'  close={hideApiSubmitModal}>
				<CardConfigure>
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__left">
										<div className={'card-configure__icon DS__icon--theme '}>
											<IconURLName />
										</div>
									</div>
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											Edit API Configuration
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div style={{ fontSize: rem(14), textAlign: 'center', marginTop: "48px", marginBottom: "48px" }}>Are you sure you want to save your changes?</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Left>
							<Button
								customeHover
								btnText
								text="Cancel"
								submit={() => hideApiSubmitModal()}
							/>
						</CardConfigure.Footer.Left>
						<CardConfigure.Footer.Right>
							<Button
								id="configuration-api-configuration-submit-button"
								text="Confirm"
								submit={() => {
								updatePitstopConfig(payload);
							}} />
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<CreateSourceSystemModal visible={isCreateSourceSystemVisible} hide={hideCreateSourceSystemModal}
				handleSubmit={handleCreateSourceSystemSubmit} />

			<DeleteSourceSystemModal visible={isDeleteSourceSystemVisible} hide={hideDeleteSourceSystemModal}
				handleSubmit={handleDeleteSourceSystemSubmit} />

			<SetDefaultSourceSystemModal visible={isSetDefaultSourceSystemVisible} hide={hideSetDefaultSourceSystemModal}
				handleSubmit={handleSetDefaultSourceSystemSubmit} />

			<Toast2 show={error} errorMessage={error} close={() => { handleError() }} />
		</>
	)
}

export default Page
